import React, { useEffect, useState, useCallback } from 'react';
import { Table, Tag, Select, Input, Button, Typography, Row, Col, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../api/client';

const { Title } = Typography;

const ROLE_LABEL: Record<string, string> = {
  admin: 'Админ',
  doctor: 'Врач',
  patient: 'Пациент',
  pharmacy_staff: 'Аптека',
};

const ROLE_COLOR: Record<string, string> = {
  admin: 'purple',
  doctor: 'blue',
  patient: 'green',
  pharmacy_staff: 'orange',
};

const LIMIT = 50;

interface User {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  iin: string | null;
  role: string;
  is_active: boolean;
  is_verified?: boolean;
  created_at: string;
}

function extractList(data: unknown): User[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.items)) return obj.items as User[];
    if (Array.isArray(obj.users)) return obj.users as User[];
    if (Array.isArray(obj.data)) return obj.data as User[];
  }
  return [];
}

function extractTotal(data: unknown): number | undefined {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    if (typeof obj.total === 'number') return obj.total;
    if (typeof obj.count === 'number') return obj.count;
  }
  return undefined;
}

export default function Users() {
  const [list, setList] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [role, setRole] = useState<string | undefined>();
  const [isActive, setIsActive] = useState<boolean | undefined>();
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const offset = (p - 1) * LIMIT;
      const params: Record<string, string | number | boolean> = {
        limit: LIMIT,
        offset,
      };
      if (role) params.role = role;
      if (isActive !== undefined) params.is_active = isActive;
      if (q.trim()) params.q = q.trim();

      const { data } = await api.get('/admin/users', { params });
      const items = extractList(data);
      setList(items);

      const backendTotal = extractTotal(data);
      if (backendTotal !== undefined) {
        setTotal(backendTotal);
      } else {
        setTotal(items.length < LIMIT ? offset + items.length : offset + LIMIT + 1);
      }
    } catch {
      message.error('Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  }, [page, role, isActive, q]);

  useEffect(() => {
    setPage(1);
    load(1);
  }, [role, isActive, q]);

  useEffect(() => {
    load(page);
  }, [page]);

  const columns: ColumnsType<User> = [
    {
      title: 'ФИО',
      dataIndex: 'full_name',
      render: (v) => v || '—',
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      render: (r: string) => (
        <Tag color={ROLE_COLOR[r] ?? 'default'}>{ROLE_LABEL[r] ?? r}</Tag>
      ),
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      render: (v) => v || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (v) => v || '—',
    },
    {
      title: 'ИИН',
      dataIndex: 'iin',
      render: (v) => v || '—',
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'default'}>{v ? 'Активен' : 'Неактивен'}</Tag>
      ),
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      render: (v) => (v ? new Date(v).toLocaleDateString('ru-RU') : '—'),
    },
  ];

  function handleReset() {
    setRole(undefined);
    setIsActive(undefined);
    setQ('');
    setSearchInput('');
  }

  return (
    <div>
      <div className="page-header">
        <Title level={4} style={{ margin: 0 }}>Пользователи</Title>
        <Button icon={<ReloadOutlined />} onClick={() => load(page)}>Обновить</Button>
      </div>

      <Row gutter={[12, 8]} className="page-filters" style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input.Search
            placeholder="Поиск (телефон, ИИН…)"
            allowClear
            style={{ width: '100%' }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(value) => setQ(value)}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            placeholder="Роль"
            allowClear
            style={{ width: '100%' }}
            value={role}
            onChange={(v) => setRole(v)}
            options={Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }))}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            placeholder="Активность"
            allowClear
            style={{ width: '100%' }}
            value={isActive}
            onChange={(v) => setIsActive(v)}
            options={[
              { value: true, label: 'Активен' },
              { value: false, label: 'Неактивен' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={3}>
          <Button block onClick={handleReset}>Сбросить</Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={list}
        rowKey="id"
        loading={loading}
        bordered
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize: LIMIT,
          total,
          onChange: setPage,
          showTotal: (t) => `Всего ~${t}`,
          responsive: true,
        }}
        locale={{ emptyText: 'Пользователей нет' }}
      />
    </div>
  );
}
